import ConfirmBox from "../../components/boxes/ConfirmBox";
import XsCont from "../../components/containers/Xs";
import { API_URL } from "../../consts/ApiUrl";
import { validationMessage } from "../../consts/ValidationMessage";
import { getAuthDetails, getCognitoUser } from "../../utils/cognitoUtils";
import { Box, Button, TextField, Typography } from "@mui/material";
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import React, { FC, useState } from "react";
import { useNavigate } from "react-router-dom";

interface CognitoError extends Error {
  code?: string;
}

type EmailConfirmStepProps = {
  onPrev: () => void;
};

const EmailConfirmStep: FC<EmailConfirmStepProps> = ({ onPrev }) => {
  const [confirmationCode, setConfirmationCode] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [resendSuccess, setResendSuccess] = useState<string>("");
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmationCode(e.target.value);
    setError("");
    setResendSuccess("");
  };
  let user: CognitoUser;
  const userDetailInfo = JSON.parse(
    sessionStorage.getItem("userDetailInfo") || "{}"
  );
  try {
    user = getCognitoUser(userDetailInfo.userId);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    navigate("/error");
  }
  const handleSubmit: () => void = async (): Promise<void> => {
    try {
      if (!confirmationCode.trim()) {
        setError(`認証コード${validationMessage.REQUIRED}`);
        return;
      }

      user.confirmRegistration(
        confirmationCode,
        true,
        async (err): Promise<void> => {
          if (err) {
            const cognitoError = err as CognitoError;
            if (cognitoError.code === "NotAuthorizedException") {
              setError(
                "すでに登録されているメールアドレスのため、メールアドレスを変更してやり直してください。"
              );
              return;
            }
            setError("認証に失敗しました。");
            return;
          }
          try {
            const userDetailInfo = JSON.parse(
              sessionStorage.getItem("userDetailInfo") || "{}"
            );
            const idToken: string = await new Promise<string>(
              (resolve, reject) => {
                const authDetails: AuthenticationDetails = getAuthDetails(
                  userDetailInfo.userId,
                  userDetailInfo.password
                );
                user.authenticateUser(authDetails, {
                  onSuccess: (session: CognitoUserSession): void => {
                    resolve(session.getIdToken().getJwtToken());
                  },
                  onFailure: (authErr) => {
                    console.error("認証失敗:", authErr);
                    reject("認証に失敗しました。");
                  },
                });
              }
            );
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { userId, password, ...filteredUserDetailInfo } =
              userDetailInfo;
            const response: Response = await fetch(API_URL.USER, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authentication: idToken,
              },
              body: JSON.stringify({
                operation: "signup",
                ...filteredUserDetailInfo,
              }),
            });
            if (!response.ok) {
              if (response.status === 401) {
                navigate("/signin");
              } else {
                navigate("/error");
              }
              return;
            }
            navigate("/signup/comp");
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
          } catch (error) {
            navigate("/error");
          }
        }
      );
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      navigate("/error");
    }
  };

  const handleResendCode: () => void = (): void => {
    try {
      setError("");
      setResendSuccess("");
      user.resendConfirmationCode((err): void => {
        if (err) {
          setError("再送信に失敗しました。");
          return;
        }
        setResendSuccess(
          "認証コードを再送信しました。メールをご確認ください。"
        );
      });
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      navigate("/error");
    }
  };

  const handleBack: () => void = (): void => {
    onPrev();
  };

  return (
    <XsCont>
      <ConfirmBox>
        <Box mt={2} mb={3} textAlign="center">
          <Typography variant="h5" gutterBottom>
            認証コード入力
          </Typography>
        </Box>

        <Box mt={2} mb={2} textAlign="center">
          <Typography variant="body2">
            登録されたメールアドレスにメールを送信しました。
            <br />
            メールをご確認の上認証コードを入力してください。
          </Typography>
        </Box>
        <TextField
          label="認証コード"
          variant="outlined"
          required
          fullWidth
          sx={{ maxWidth: "320px", marginTop: "5px" }}
          value={confirmationCode}
          onChange={handleChange}
          error={!!error}
          helperText={error || resendSuccess}
        />
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleSubmit}
          sx={{ maxWidth: "320px", marginTop: "15px" }}
        >
          認証
        </Button>
        <Button
          variant="outlined"
          color="primary"
          fullWidth
          onClick={handleResendCode}
          sx={{ maxWidth: "320px", marginTop: "3px", marginBottom: "20px" }}
        >
          認証コードを再度送信
        </Button>
        <Button
          variant="outlined"
          color="primary"
          fullWidth
          onClick={handleBack}
          sx={{ maxWidth: "320px", marginTop: "3px", marginBottom: "20px" }}
        >
          戻る
        </Button>
      </ConfirmBox>
    </XsCont>
  );
};

export default EmailConfirmStep;
